import React, { useEffect, useRef, CSSProperties } from 'react'
import { X } from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  width?: string | number
  closeOnEscape?: boolean
  closeOnBackdrop?: boolean
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '12px',
  backdropFilter: 'blur(2px)',
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = 560,
  closeOnEscape = true,
  closeOnBackdrop = true,
}) => {
  const shouldCloseFromBackdropRef = useRef(false)

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [closeOnEscape, isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (!closeOnBackdrop) {
          shouldCloseFromBackdropRef.current = false
          return
        }
        shouldCloseFromBackdropRef.current = event.target === event.currentTarget && event.button === 0
      }}
      onClick={(event) => {
        if (!closeOnBackdrop) return
        if (!shouldCloseFromBackdropRef.current) return
        shouldCloseFromBackdropRef.current = false
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        onMouseDown={() => {
          shouldCloseFromBackdropRef.current = false
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff',
          borderRadius: '8px',
          width: typeof width === 'number' ? `${width}px` : width,
          maxWidth: '100%',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 18px 48px rgba(0,0,0,0.24)',
          animation: 'modalIn 0.2s ease',
        }}
      >
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: translateY(-10px) scale(0.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
        {title && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 22px',
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          >
            <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#1a202c', margin: 0 }}>
              {title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#718096',
                display: 'flex',
                padding: '4px',
                borderRadius: '4px',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <X size={18} />
            </button>
          </div>
        )}
        <div style={{ padding: '22px', overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>
  )
}
